import { useState } from "react";
import { format, parseISO } from "date-fns";
import { AlertTriangle, Lightbulb, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { toLocal } from "@/lib/time";

export default function AiPanel({ insight, setInsight, ws, viewUserId }) {
  const [busy, setBusy] = useState(false);

  const ask = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/suggest", { week_start: ws, now: toLocal(new Date()), user_id: viewUserId || null });
      setInsight(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-2xl border border-[#103080]/10 bg-[#F5F2EB] ambient p-6 fade-up" data-testid="ai-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#0090D0] font-semibold inline-flex items-center gap-1.5"><Sparkles size={12} /> AI planning assistant</p>
          <h2 className="font-serif-brand font-semibold text-xl text-[#103080] mt-1">How does my week <i className="text-[#0090D0]">look?</i></h2>
        </div>
      </div>
      {busy ? (
        <div className="py-8 flex flex-col items-center gap-3 text-sm text-[#103080]/70" data-testid="ai-loading">
          <Loader2 className="animate-spin text-[#0090D0]" /> Reviewing your deadlines and schedule…
        </div>
      ) : insight ? (
        <div className="mt-4 space-y-4" data-testid="ai-insight">
          <p className="text-sm text-[#0F172A]/80 leading-relaxed" data-testid="ai-summary">{insight.summary}</p>
          {insight.focus_now && (
            <p className="text-sm rounded-xl bg-white p-3 border border-[#103080]/10 text-[#103080]" data-testid="ai-focus-now">
              <span className="font-semibold">Next: </span>{insight.focus_now}
            </p>
          )}
          {insight.risks?.length > 0 && (
            <ul className="space-y-2" data-testid="ai-risks">
              {insight.risks.map((r, i) => (
                <li key={i} className="rounded-xl border border-amber-300/70 bg-amber-50/70 p-3">
                  <p className="text-sm font-semibold text-amber-800 inline-flex items-center gap-1.5"><AlertTriangle size={13} />{r.title}</p>
                  <p className="text-xs text-amber-900/80 mt-1 leading-relaxed">{r.detail}</p>
                </li>
              ))}
            </ul>
          )}
          {insight.suggestions?.length > 0 && (
            <ul className="space-y-2" data-testid="ai-suggestions">
              {insight.suggestions.map((s, i) => (
                <li key={i} className="flex gap-2 text-sm text-[#0F172A]/75 leading-relaxed">
                  <Lightbulb size={14} className="text-[#0090D0] mt-1 shrink-0" />{s}
                </li>
              ))}
            </ul>
          )}
          <p className="text-[11px] text-[#0F172A]/40">Updated {format(parseISO(insight.created_at), "EEE d MMM, HH:mm")}</p>
        </div>
      ) : (
        <p className="mt-4 text-sm text-[#0F172A]/60 leading-relaxed" data-testid="ai-empty">
          Get a plain-English review of your week: what's at risk, what to tackle first, and how to make it fit.
        </p>
      )}
      <button onClick={ask} disabled={busy} data-testid="ai-suggest-button"
        className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full border border-[#103080]/20 bg-white text-[#103080] font-medium h-11 text-sm hover:bg-[#103080] hover:text-white transition-colors duration-200 disabled:opacity-60">
        <Sparkles size={15} /> {insight ? "Refresh suggestions" : "Get AI suggestions"}
      </button>
    </section>
  );
}
